import React, { Component } from 'react'
import axios from 'axios'
import { Redirect } from 'react-router-dom'
import isEqual from 'lodash.isequal'
import ApiClient from '@softwaredev_cegeka/api-client'
import AuthApi from '@softwaredev_cegeka/auth-api'
import {
    getExpDateFromToken,
    getIsOwningTenantFromToken,
    getChildOrgIdsFromToken,
    getCompanyIDFromToken,
} from 'src/utils/jwtTokenUtils'
import * as serviceWorkerRegistration from 'src/serviceWorkerRegistration'
import { clearApiCache, unregister } from 'src/serviceWorkerRegistration'
import SessionExpiredModal from 'src/components/SessionExpiredModal'
import { pushGtmDataLayer } from 'src/utils/gtm'
import getEnvVariableValue, { VARIABLES } from 'src/utils/getEnvVariableValue'

let __userData = {}

/**
 * Returns the userdata.
 * Always prefer using the React UserData context where possible!
 * This is only intended to be used by non-react code, as this does not trigger an update when it changes!
 */
export function getUserData() {
    return __userData
}

/**
 * Consume this context or use the withUserData HOC to access the info about the logged-in user
 * @type {React.Context<{userData: null, permissions: null, authToken: null, setAuthToken: setAuthToken, shouldSeeImpersonator: boolean}>}
 */
export const UserDataContext = React.createContext({
    authToken: null,
    permissions: null,
    data: {
        firstName: 'anonymous',
        lastName: 'guest',
        impersonateOrganization: () => {},
        stopImpersonateOrganization: () => {},
    },
    /**
     * @deprecated Use the rest of UserData, not the response directly. This only exist to ease ccp2 integration.
     */
    ssoResponse: null,
    setAuthToken: () => {},
    shouldSeeImpersonator: false,
})

/**
 * @type {React.Context<{subsidiaries: *[], currentGlobalSubsidiary: null}>}
 */
export const SubsidiaryDataContext = React.createContext({
    currentGlobalSubsidiary: null,
    subsidiaries: [],
})

/**
 * @deprecated use only for CCP2 integration.
 * @type {React.Context<null>}
 */
export const SsoResponseContext = React.createContext(null)

/**
 * Cache non-confidential userdata for offline mode
 * @param userData
 */
function cacheUserData(userData) {
    const {
        firstName,
        lastName,
        email,
        username,
        is_global_admin,
        is_local_user,
        organization_id,
    } = userData
    // Manually set all the values we can store to prevent leaking sensitive data
    localStorage.setItem(
        'horizon-user-data-cache',
        JSON.stringify({
            firstName,
            lastName,
            email,
            username,
            is_global_admin,
            is_local_user,
            organization_id,
        })
    )
}

/**
 * Cache permissions for offline mode
 * @param permissions
 */
function cachePermissions(permissions) {
    localStorage.setItem(
        'horizon-user-permissions-cache',
        JSON.stringify(permissions)
    )
}

/**
 * HOC/context provider hybrid which enforces authentication and adds a UserDataProvider
 * @param ComponentToProtect
 */
export default function withAuth(ComponentToProtect) {
    return class extends Component {
        constructor(props, context) {
            super(props, context)
            this.sessionExpTimeout = null
            this.authApi = new AuthApi()
            this.state = {
                loading: true,
                authorized: true,
                sessionExpired: false,
                ssoResponse: null,
                subsidiaryData: {
                    currentGlobalSubsidiary: null,
                    subsidiaries: [],
                    setData: (subsidiaryData, callback) => {
                        this.setState(
                            {
                                subsidiaryData: {
                                    ...this.state.subsidiaryData,
                                    ...subsidiaryData,
                                },
                            },
                            callback
                        )
                    },
                    setGlobalSubsidiary: subsidiary =>
                        this.state.subsidiaryData.setData({
                            currentGlobalSubsidiary: subsidiary,
                        }),
                    setSubsidiaries: data => {
                        return new Promise(resolve => {
                            const currentToken = data.isImpersonating
                                ? data.impersonationToken
                                : data.token
                            const currentUser = data.isImpersonating
                                ? data.impersonationUserData.username
                                : data.user.username

                            const childOrgIds =
                                getChildOrgIdsFromToken(currentToken)

                            Promise.all([
                                this.authApi.fetchTenants({
                                    username: currentUser,
                                }),
                                this.authApi.fetchSubtenants({
                                    username: currentUser,
                                }),
                            ]).then(response => {
                                const organizations = response[0].data.tenants
                                const subtenants = response[1].data.subtenants

                                this.state.userData.setData({
                                    tenants: organizations,
                                    subtenants: subtenants,
                                })

                                if (childOrgIds && childOrgIds.length) {
                                    const userDataOrganizationId =
                                        data.isImpersonating
                                            ? data.impersonationUserData
                                                  .organization_id
                                            : data.user.organization_id

                                    const defaultOrganization =
                                        organizations.find(
                                            organization =>
                                                organization.organization_id ===
                                                userDataOrganizationId
                                        )

                                    this.state.subsidiaryData.setData(
                                        {
                                            currentGlobalSubsidiary: {
                                                id: defaultOrganization.organization_id,
                                                label: defaultOrganization.name,
                                                value: defaultOrganization.organization_id,
                                            },
                                            subsidiaries: [
                                                {
                                                    id: defaultOrganization.organization_id,
                                                    label: defaultOrganization.name,
                                                    value: defaultOrganization.organization_id,
                                                },
                                                ...subtenants.map(
                                                    subtenant => ({
                                                        id: subtenant.organization_id,
                                                        label: subtenant.name,
                                                        value: subtenant.organization_id,
                                                    })
                                                ),
                                            ],
                                        },
                                        resolve
                                    )
                                } else {
                                    this.state.subsidiaryData.setData(
                                        {
                                            currentGlobalSubsidiary: null,
                                            subsidiaries: [],
                                        },
                                        resolve
                                    )
                                }
                            })
                        })
                    },
                },
                userData: {
                    authToken: null,
                    permissions: null,
                    data: null,
                    shouldSeeImpersonator: false,
                    isImpersonating: false,
                    defaultOrg: null,
                    impersonatedOrganization: null,
                    setIsImpersonating: isImpersonating => {
                        this.setState({
                            userData: {
                                ...this.state.userData,
                                isImpersonating: isImpersonating,
                            },
                        })
                    },
                    setData: userData => {
                        this.setState(
                            {
                                userData: {
                                    ...this.state.userData,
                                    ...userData,
                                },
                            },
                            () => {
                                __userData = this.state.userData
                            }
                        )
                    },
                    setPermissions: permissions => {
                        this.state.userData.setData({
                            permissions: permissions,
                        })
                    },
                    setAuthToken: authToken => {
                        this.state.userData.setData({
                            authToken: authToken,
                        })
                    },
                    // Set either defaultOrg or impersonatedOrganization based on context
                    setOrganization: (org, isImpersonating) => {
                        if (isImpersonating) {
                            this.state.userData.setData({
                                impersonatedOrganization: org,
                                defaultOrg: null,
                            })
                        } else {
                            this.state.userData.setData({
                                defaultOrg: org,
                                impersonatedOrganization: null,
                            })
                        }
                    },
                    setShouldSeeImpersonator: data => {
                        this.state.userData.setData({
                            shouldSeeImpersonator:
                                Boolean(data.orig_sub).valueOf() ||
                                Boolean(data.orig_organization_id).valueOf() ||
                                getIsOwningTenantFromToken(
                                    this.state.userData.authToken
                                ),
                        })
                    },
                    stopImpersonateOrganization: async () => {
                        try {
                            await this.authApi.stopImpersonateOrganization()
                            await clearApiCache()
                            window.location.reload()
                        } catch (e) {
                            console.error(e)
                        }
                    },
                    /**
                     * @param organizationId
                     * @returns {Promise<void>}
                     */
                    impersonateOrganization: async organizationId => {
                        try {
                            await this.authApi.impersonateOrganization({
                                organization_id: organizationId,
                            })
                            await clearApiCache()
                            window.location.reload()
                        } catch (e) {
                            console.error(e)
                        }
                    },
                    /**
                     * @param username
                     * @returns {Promise<void>}
                     */
                    impersonateUser: async username => {
                        try {
                            await this.authApi.impersonateUser({
                                username: username,
                            })
                            await clearApiCache()
                            window.location.reload()
                        } catch (e) {
                            console.error(e)
                        }
                    },
                    stopImpersonateUser: async () => {
                        try {
                            await this.authApi.stopImpersonateUser()
                            await clearApiCache()
                            localStorage.removeItem('organizations')
                            window.location.reload()
                        } catch (e) {
                            console.error(e)
                        }
                    },
                    /**
                     * Impersonate both user and org simultaneously
                     * @param username
                     * @param organizationId
                     * @returns {Promise<void>}
                     */
                    impersonate: async (username, organizationId) => {
                        try {
                            await this.authApi.impersonateUser({
                                username,
                                organization_id: organizationId,
                            })
                            await clearApiCache()
                            window.location.reload()
                        } catch (e) {
                            console.error(e)
                        }
                    },
                },
            }
        }

        /**
         * @return {Promise<void>}
         */
        async componentDidMount() {
            const { authToken } = this.state.userData
            if (authToken) {
                if (this.state.loading) {
                    this.setState({ loading: false })
                }
                return
            }

            await this.checkAuthorization()
        }

        /**
         * @param prevProps
         * @param prevState
         */
        componentDidUpdate(prevProps, prevState) {
            // Keep userData and permissions cached so we can still do API calls when offline.
            const prevUserData = prevState.userData?.data
            const userData = this.state.userData?.data

            // Skip if we are impersonating
            if (userData && !isEqual(prevUserData, userData)) {
                cacheUserData(userData)
            }

            const prevPermissions = prevState.userData?.permissions
            const permissions = this.state.userData?.permissions
            if (this.state.userData?.authToken && !this.sessionExpTimeout) {
                const delay =
                    getExpDateFromToken(this.state.userData?.authToken) -
                    Date.now()
                this.sessionExpTimeout = setTimeout(() => {
                    // Session expired
                    this.setState({ sessionExpired: true })
                }, delay)
            }
            if (permissions && !isEqual(prevPermissions, permissions)) {
                cachePermissions(permissions)
            }
        }

        componentWillUnmount() {
            clearTimeout(this.sessionExpTimeout)
            this.setState({ sessionExpired: false })
        }

        /**
         * Fetch and set organization data using existing localStorage pattern
         * This follows the same pattern as the existing code for organizations
         */
        async fetchAndSetOrganizationData(
            username,
            organizationId,
            isImpersonating
        ) {
            try {
                let organizations = localStorage.getItem('organizations')
                    ? JSON.parse(localStorage.getItem('organizations'))
                    : null

                // If not in localStorage, fetch from API
                if (!organizations) {
                    const orgResponse = await this.authApi.fetchTenants({
                        username,
                    })
                    organizations = orgResponse.data.tenants
                    localStorage.setItem(
                        'organizations',
                        JSON.stringify(organizations)
                    )
                }

                // Find the current organization
                const currentOrg = organizations.find(
                    org => org.organization_id === organizationId
                )

                if (currentOrg) {
                    const orgData = {
                        label: currentOrg.name,
                        value: organizationId,
                    }
                    // Set either defaultOrg or impersonatedOrganization, not both
                    this.state.userData.setOrganization(
                        orgData,
                        isImpersonating
                    )
                }
            } catch (error) {
                console.error('Error fetching organization data:', error)
            }
        }

        /**
         * @return {Promise<void>}
         */
        async checkAuthorization() {
            await this.resetUserData()
            const { userData, subsidiaryData } = this.state
            const {
                setPermissions,
                setAuthToken,
                setData,
                setShouldSeeImpersonator,
                setIsImpersonating,
            } = userData
            const { setSubsidiaries } = subsidiaryData

            if (localStorage.getItem('mobile-auth-token')) {
                setPermissions(
                    JSON.parse(localStorage.getItem('mobile-auth-permissions'))
                )
                setAuthToken(localStorage.getItem('mobile-auth-token'))
                setData(JSON.parse(localStorage.getItem('mobile-auth-data')))
                this.setState({ loading: false })
                return
            }
            const res = await axios.get('/_sso-login')
            try {
                const { token } = res.data
                if (!token) {
                    localStorage.setItem('showNewFeature', 1)
                    try {
                        await clearApiCache()
                    } catch (e) {
                        console.error(e)
                    } finally {
                        window.location.href = '/_sso-login'
                    }
                    return
                }

                if (res.data.permissions && res.data.permissions.length !== 0) {
                    setPermissions(
                        res.data.isImpersonating
                            ? res.data.impersonationPermissions
                            : res.data.permissions
                    )
                } else {
                    this.setState({
                        loading: false,
                        authorized: false,
                        ssoResponse: res,
                    })

                    // If we don't have permissions for this app, unregister the sw.
                    if ('serviceWorker' in navigator) {
                        await serviceWorkerRegistration.unregister()
                    }

                    return
                }

                const policyVersion = getEnvVariableValue(
                    VARIABLES.COOKIE_POLICY_VERSION
                )

                if (
                    !document.cookie.includes(`policyConsent=${policyVersion}`)
                ) {
                    unregister()
                        .then(() => {
                            console.log(
                                'Consent not found. Removed serviceworker.'
                            )
                            window.location.href = '/'
                        })
                        .catch(err => {
                            console.error(err)
                        })
                }

                // Get the expiration date of the token and save that to local storage
                localStorage.setItem(
                    'horizon-token-expiration',
                    getExpDateFromToken(token)
                )

                // Reset sessionExpired after updating a new token in local storage.
                this.setState({ sessionExpired: false })

                // This event will only be fired when another tab changes localStorage, not the current one
                window.addEventListener('storage', e => {
                    if (
                        e.key === 'horizon-token-expiration' &&
                        Number(e.newValue) < Number(new Date())
                    ) {
                        if (e.newValue === '0') {
                            // Logout was probably called and, we want to show the expired message.
                            // Everytime logout is called the horizon-token-expiration is set to 0.
                            this.setState({ sessionExpired: true })
                        } else {
                            window.location.href = '/unauthorized'
                        }
                    }
                })

                const authToken = res.data.isImpersonating
                    ? res.data.impersonationToken
                    : res.data.token
                setAuthToken(authToken)

                // Register the service-worker after authentication to ensure we don't cache assets when you don't have permissions.
                serviceWorkerRegistration.register({
                    onSuccess: registration =>
                        registration.active.postMessage({
                            type: 'IS_IMPERSONATING',
                            value: res.data.isImpersonating,
                        }),
                })
                const currentUser = res.data.isImpersonating
                    ? res.data.impersonationUserData
                    : res.data.user
                if (currentUser) {
                    const {
                        firstName,
                        lastName,
                        email,
                        username,
                        is_global_admin,
                        is_local_user,
                        organization_id,
                        orig_organization_id = null,
                        orig_sub = null,
                    } = currentUser
                    const data = {
                        firstName,
                        lastName,
                        email,
                        username,
                        is_global_admin,
                        is_local_user,
                        organization_id,
                        orig_organization_id,
                        orig_sub,
                        company_id: getCompanyIDFromToken(authToken),
                    }

                    setData({ data })
                    await this.fetchAndSetOrganizationData(
                        username,
                        organization_id,
                        res.data.isImpersonating
                    )
                    pushGtmDataLayer({
                        event: 'authentication',
                        isOwningTenant: data.isOwningTenant,
                        companyId: data.company_id,
                        isGlobalAdmin: is_global_admin,
                        isLocalUser: is_local_user,
                        organizationId: organization_id,
                        isImpersonating: !!res.data.isImpersonating,
                    })
                    setShouldSeeImpersonator(currentUser)

                    if (res.data.isImpersonating) {
                        ApiClient.setImpersonatorHeaderValue(
                            `${organization_id}_${username}`
                        )
                    } else {
                        ApiClient.setImpersonatorHeaderValue(null)
                    }

                    setIsImpersonating(!!res.data.isImpersonating)
                    await setSubsidiaries(res.data)
                    this.setState({ loading: false, ssoResponse: res })
                }
            } catch (err) {
                console.error(err)
                this.setState({ loading: false, authorized: false })
            }
        }

        /**
         * @return {Promise<unknown>}
         */
        resetUserData() {
            return new Promise(resolve => {
                this.setState(
                    state => ({
                        loading: true,
                        authorized: true,
                        userData: {
                            ...state.userData,
                            authToken: null,
                            permissions: null,
                            data: null,
                        },
                        ssoResponse: null,
                    }),
                    resolve
                )
            })
        }

        /**
         * @return {JSX.Element|null}
         */
        render() {
            const {
                authorized,
                loading,
                sessionExpired,
                subsidiaryData,
                userData,
                ssoResponse,
            } = this.state
            if (loading || !ssoResponse) {
                return null
            }
            if (!authorized) {
                return <Redirect to={'/unauthorized'} />
            }
            return (
                <SsoResponseContext.Provider value={ssoResponse}>
                    <SubsidiaryDataContext.Provider value={subsidiaryData}>
                        <UserDataContext.Provider value={userData}>
                            <ComponentToProtect {...this.props} />
                            <SessionExpiredModal
                                sessionExpired={sessionExpired}
                                onLoginClick={() => this.checkAuthorization()}
                            />
                        </UserDataContext.Provider>
                    </SubsidiaryDataContext.Provider>
                </SsoResponseContext.Provider>
            )
        }
    }
}
